import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import { CampaignBid, PreviewResponse } from './types';

/** Pretty-print a param that the API takes as a JSON string. */
const BID_NAMES: Record<string, string> = {
  COST_CAP: 'Cost cap',
  LOWEST_COST_WITH_BID_CAP: 'Bid cap',
  LOWEST_COST_WITH_MIN_ROAS: 'Min ROAS',
};

/** "Cost cap $95.00", "Min ROAS 1.5". The US account is in dollars. */
export function bidLabel(bid: CampaignBid): string {
  const name = BID_NAMES[bid.strategy] || bid.strategy;
  if (bid.amount != null) return `${name} $${(bid.amount / 100).toFixed(2)}`;
  const floor = bid.constraints?.roas_average_floor;
  return floor != null ? `${name} ${floor / 10000}` : name;
}

function parsed(value: string | undefined): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function Json({ value }: { value: unknown }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        fontSize: 11,
        lineHeight: 1.5,
        bgcolor: 'action.hover',
        borderRadius: 1,
        overflowX: 'auto',
        maxHeight: 320,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  );
}

/**
 * Exactly what would be posted to Meta, before anything is.
 *
 * The point is that the operator can check the parts that are easy to get
 * wrong and expensive to notice later — which campaign, which ad set name,
 * paused or active, and whether the enhancements really are off.
 */
export default function PreviewDrawer({
  open,
  onClose,
  preview,
  error,
}: {
  open: boolean;
  onClose: () => void;
  preview: PreviewResponse | null;
  error?: string;
}) {
  const firstAd = preview?.payload.ads[0];
  const feedSpec = parsed(firstAd?.creativeParams.asset_feed_spec) as any;
  const dof = parsed(firstAd?.creativeParams.degrees_of_freedom_spec) as any;
  const features: Record<string, { enroll_status: string }> = dof?.creative_features_spec ?? {};
  const optedIn = Object.entries(features).filter(([, v]) => v.enroll_status === 'OPT_IN').map(([k]) => k);

  return (
    // The app bar is position="fixed" at zIndex "modal" (1300) and a Drawer
    // defaults to 1200, so without this it paints OVER the drawer's own header
    // and "Launch preview" plus the close button sit behind it. Matching 1300
    // is enough because the drawer's portal is appended after the app bar, and
    // it is what the Attribution and Order Kitting drawers already do.
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1300 }}
      PaperProps={{ sx: { width: { xs: '100%', md: 720 } } }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Launch preview</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        {preview && (
          <>
            <Stack direction="row" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip size="small" label={preview.campaign.name} />
              <Chip size="small" color="primary" label={preview.adsetName} />
              <Chip size="small" variant="outlined" label={`template: ${preview.templateName}`} />
              <Chip size="small" color={preview.adStatus === 'ACTIVE' ? 'warning' : 'default'} label={preview.adStatus} />
              <Chip size="small" variant="outlined" label={`${preview.payload.ads.length} ads`} />
              <Chip size="small" variant="outlined" label={preview.mediaKind} />
              {preview.campaign.bid && (
                <Chip size="small" variant="outlined" label={`${bidLabel(preview.campaign.bid)} (as in ${preview.campaign.bid.fromAdset.name})`} />
              )}
            </Stack>

            <Alert severity={preview.siteLinkCount ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {preview.siteLinkCount
                ? `${preview.siteLinkCount} site links will be attached — curate them on the Settings tab.`
                : 'No site links are being sent; Meta will suggest its own, as it does today.'}
            </Alert>
            {preview.adStatus === 'ACTIVE' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                These ads will be created ACTIVE and can start spending against the campaign budget from{' '}
                {preview.startTime}.
              </Alert>
            )}

            <Typography variant="subtitle2">Ad set</Typography>
            <Json value={Object.fromEntries(Object.entries(preview.payload.adset).map(([k, v]) => [k, parsed(v) ?? v]))} />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Creative enhancements
            </Typography>
            {optedIn.length === 0 ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                All {Object.keys(features).length} enhancements are set to OPT_OUT, including Advantage+ creative.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Left ON: {optedIn.join(', ')}
              </Alert>
            )}

            <Typography variant="subtitle2" gutterBottom>Placement split</Typography>
            <Json
              value={(feedSpec?.asset_customization_rules ?? []).map((r: any) => ({
                priority: r.priority,
                placements: r.customization_spec?.publisher_platforms
                  ? {
                    platforms: r.customization_spec.publisher_platforms,
                    facebook: r.customization_spec.facebook_positions,
                    instagram: r.customization_spec.instagram_positions,
                  }
                  : 'everything else',
                asset: r.image_label?.name ?? r.video_label?.name,
              }))}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Ads</Typography>
            {preview.payload.ads.map(ad => {
              const spec = parsed(ad.creativeParams.asset_feed_spec) as any;
              return (
                <Box key={ad.notionPageId} sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{ad.name}</Typography>
                  <Json
                    value={{
                      headlines: (spec?.titles ?? []).map((t: any) => t.text),
                      captions: (spec?.bodies ?? []).map((b: any) => b.text),
                      link: (spec?.link_urls ?? [])[0]?.website_url,
                      cta: spec?.call_to_action_types,
                    }}
                  />
                </Box>
              );
            })}
          </>
        )}
      </Box>
    </Drawer>
  );
}
